import { useSyncExternalStore, useCallback } from 'react';

/**
 * A hook that listens to a CSS media query and returns whether it matches.
 *
 * This hook is SSR-safe and returns `false` during server-side rendering
 * or in environments where `window` is not available.
 *
 * @param query - A valid CSS media query string (e.g., '(max-width: 768px)')
 * @returns `true` if the media query matches, `false` otherwise
 *
 * @example
 * ```tsx
 * // Check if viewport is mobile-sized
 * const isMobile = useMediaQuery('(max-width: 768px)');
 *
 * // Check if viewport is desktop-sized
 * const isDesktop = useMediaQuery('(min-width: 769px)');
 *
 * // Check for reduced motion preference
 * const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
 *
 * // Conditional rendering based on viewport
 * return isMobile ? <MobileLayout /> : <DesktopLayout />;
 * ```
 */
export function useMediaQuery(query: string): boolean {
  // Use useSyncExternalStore for proper subscription to browser media query
  // This is the recommended React 18 approach for subscribing to external stores
  const subscribe = useCallback(
    (callback: () => void) => {
      // Guard against SSR
      if (typeof window === 'undefined') {
        return () => {};
      }

      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener('change', callback);

      return () => {
        mediaQueryList.removeEventListener('change', callback);
      };
    },
    [query]
  );

  const getSnapshot = useCallback(() => {
    // Guard against SSR
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);

  const getServerSnapshot = useCallback(() => {
    // Always return false for SSR
    return false;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
